import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { environment } from "src/environments/environment";
import {
  AppNotification,
  NOTIFICATION_SOCKET_EVENTS,
  NotificationUnreadCountEvent,
} from "src/app/core/models/notification.model";
import { NotificationSocketFactory } from "./notification-socket-factory.service";
import { NotificationRealtimeTokenService } from "./notification-realtime-token.service";
import {
  NotificationConnectErrorEvent,
  NotificationSocket,
  NotificationSocketAuthPayload,
  NOTIFICATION_TOKEN_EXPIRED_MESSAGE,
  NOTIFICATION_UNAUTHORIZED_PREFIX,
} from "./notification-socket.types";

/**
 * Realtime channel for the notification center (issue #416): connects to
 * flymsg-node's `/notifications` Socket.IO namespace and republishes
 * `notification:new` / `notification:unread-count` as observables.
 *
 * Every public method is a no-op unless {@link connect} has been called —
 * `AppNotificationService` only calls `connect()` after its own beta gate
 * (`userHasNotificationCenterAccess`) has already passed, so this service
 * never opens a socket for a non-beta user. When the namespace is
 * unreachable (today: certain, since flymsg-be does not yet mint a
 * `"notifications"`-scoped token — see `NotificationRealtimeTokenService`)
 * {@link connected$} emits `false` and stays `false`; it never throws and
 * never spams the console beyond a single debug-level log per attempt.
 */
@Injectable({ providedIn: "root" })
export class NotificationSocketService {
  private socket: NotificationSocket | null = null;
  private socketReady: Promise<NotificationSocket> | null = null;

  private connectedSubject = new BehaviorSubject<boolean>(false);
  private notificationNewSubject = new Subject<AppNotification>();
  private unreadCountSubject = new Subject<number>();

  /** Whether the realtime channel is currently connected. */
  connected$ = this.connectedSubject.asObservable();
  /** Emits a notification the moment flymsg-node pushes it. */
  notificationNew$ = this.notificationNewSubject.asObservable();
  /** Emits the server's authoritative unread count on every push. */
  unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(
    private readonly socketFactory: NotificationSocketFactory,
    private readonly tokenService: NotificationRealtimeTokenService
  ) {}

  get isConnected(): boolean {
    return this.socket?.connected === true;
  }

  /** Lazily connects to the notification socket. Idempotent. */
  connect(): void {
    if (this.socketReady) {
      return;
    }

    this.socketReady = this.socketFactory
      .create(`${environment.NODE_API_URL}/notifications`, {
        // Callback form → a fresh realtime JWT is minted for every
        // (re)connect handshake, not just the first. On a mint failure send
        // an empty token: the gateway rejects with `unauthorized: missing
        // token`, surfaced via `connect_error` like any other rejection.
        auth: (cb: (payload: NotificationSocketAuthPayload) => void) =>
          this.tokenService
            .getRealtimeToken()
            .then((token) => cb({ token }))
            .catch(() => cb({ token: "" })),
      })
      .then((socket) => {
        this.socket = socket;
        this.wireSocket(socket);
        socket.connect();
        return socket;
      })
      .catch(() => {
        this.socketReady = null;
        this.connectedSubject.next(false);
        return null as unknown as NotificationSocket;
      });
  }

  /** Disconnects the socket entirely (e.g. on bell/logout teardown). */
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.socketReady = null;
    this.connectedSubject.next(false);
  }

  private wireSocket(socket: NotificationSocket): void {
    socket.on("connect", () => this.connectedSubject.next(true));
    socket.on("disconnect", () => this.connectedSubject.next(false));

    socket.on(NOTIFICATION_SOCKET_EVENTS.NEW, (payload: AppNotification) => {
      this.notificationNewSubject.next(payload);
    });

    socket.on(NOTIFICATION_SOCKET_EVENTS.UNREAD_COUNT, (payload: NotificationUnreadCountEvent) => {
      this.unreadCountSubject.next(payload?.count ?? 0);
    });

    socket.on("connect_error", (evt: NotificationConnectErrorEvent) => {
      this.handleConnectError(socket, evt?.message ?? "");
    });
  }

  /**
   * Handles a Socket.IO `connect_error`, mirroring
   * flymsg-roleplay's `RoleplayCopilotService` (issue #54):
   * - `unauthorized: token expired` — drop the cached token; Socket.IO's own
   *   reconnection retries and the `auth` callback mints a fresh one.
   * - any other `unauthorized: *` — not recoverable by retrying (missing
   *   scope, bad signature, revoked). Stop reconnecting.
   * - anything else (transport/network) — leave Socket.IO's default
   *   reconnection (exponential backoff) running; `connected$` is already
   *   `false`, which is all the caller needs to fall back to polling.
   */
  private handleConnectError(socket: NotificationSocket, message: string): void {
    this.connectedSubject.next(false);

    if (message === NOTIFICATION_TOKEN_EXPIRED_MESSAGE) {
      this.tokenService.invalidate();
      return;
    }
    if (message.startsWith(NOTIFICATION_UNAUTHORIZED_PREFIX)) {
      socket.disconnect();
    }
  }
}
